import { matchPath } from 'react-router-dom';
import {
  ROUTES_PATH
  // globalConstantValues,
} from '../constants/globalConstant';

// ---------------------------------------------------------------------------
// Lazy web-feature loaders.
//
// Each web feature's config lives with its view code
// (src/views/<Feature>/featureConfig.js). Webpack code-splits each one,
// so opening e.g. /launchbars ships LaunchBar's service chunk only — not
// CasaWebHomepage / Object360 / TopNavigation chunks.
// ---------------------------------------------------------------------------

const LAZY_FEATURE_LOADERS = {
  launchBar: () =>
    import(/* webpackChunkName: "feature-launchbar" */ '../views/LaunchBar/featureConfig'),
  topNavigation: () =>
    import(/* webpackChunkName: "feature-topnav" */ '../views/TopNavigation/featureConfig'),
  object360: () =>
    import(/* webpackChunkName: "feature-object360" */ '../views/Object360/featureConfig'),
  webHomepage: () =>
    import(/* webpackChunkName: "feature-webhomepage" */ '../views/CasaWebHomepage/featureConfig'),
  announcements: () =>
    import(/* webpackChunkName: "feature-announcements" */ '../views/Announcements/featureConfig'),
  menuBuilder: () =>
    import(/* webpackChunkName: "feature-menubuilder" */ '../views/MenuBuilder/featureConfig'),
  homepage: () =>
    import(/* webpackChunkName: "feature-homepage" */ '../views/HomepagesView/featureConfig'),
  leadControls: () =>
    import(/* webpackChunkName: "feature-leadcontrols" */ '../views/LeadControls/featureConfig')
};

// Route descriptors for lazy features — kept in sync with each feature's
// own featureConfig.js. Used for sync route matching; the full config loads
// on demand.
const LAZY_FEATURE_ROUTES = [
  {
    key: 'launchBar',
    listingRoutes: [ROUTES_PATH.launchBar],
    builderRoutes: [`${ROUTES_PATH.launchBarHomePage}/:launchbarId?`]
  },
  {
    key: 'topNavigation',
    listingRoutes: [ROUTES_PATH.topNavigation],
    builderRoutes: [`${ROUTES_PATH.topNavigationBuilder}/:topNavId?`]
  },
  {
    key: 'object360',
    listingRoutes: [ROUTES_PATH.object360],
    builderRoutes: [`${ROUTES_PATH.object360Builder}/:object360Id?`]
  },
  {
    key: 'webHomepage',
    listingRoutes: [ROUTES_PATH.webDashboards],
    builderRoutes: [`${ROUTES_PATH.webHomepageBuilder}/:dashboardId?`]
  },
  {
    key: 'announcements',
    listingRoutes: [ROUTES_PATH.announcements],
    builderRoutes: [`${ROUTES_PATH.announcementBuilder}/:announcementId?`]
  },
  {
    key: 'menuBuilder',
    listingRoutes: [ROUTES_PATH.menus],
    builderRoutes: [`${ROUTES_PATH.menubuilder}/:menuId?`]
  },
  {
    key: 'homepage',
    listingRoutes: [ROUTES_PATH.homepages],
    builderRoutes: [`${ROUTES_PATH.homebuilder}/:homepageId?`]
  },
  {
    key: 'leadControls',
    listingRoutes: [ROUTES_PATH.leadControls],
    builderRoutes: [`${ROUTES_PATH.leadControlBuilder}/:leadControlId?`]
  }
];

// Module-level cache. Entries are either a Promise (in flight) or the
// resolved config object. `null` means the key isn't a lazy feature.
const lazyConfigCache = new Map();

function loadLazyFeatureConfig(key) {
  if (lazyConfigCache.has(key)) return lazyConfigCache.get(key);
  const loader = LAZY_FEATURE_LOADERS[key];
  if (!loader) return null;
  const promise = loader().then((module) => {
    const cfg = module.default;
    lazyConfigCache.set(key, cfg);
    return cfg;
  });
  lazyConfigCache.set(key, promise);
  return promise;
}

function getLazyFeatureConfigSync(key) {
  const entry = lazyConfigCache.get(key);
  // Only resolved configs are returned synchronously. In-flight promises
  // return undefined so callers trigger a suspend.
  return entry && !(entry instanceof Promise) ? entry : undefined;
}

// ---------------------------------------------------------------------------
// Route matchers
// ---------------------------------------------------------------------------

function matchRoute(pathname, listingRoutes, builderRoutes) {
  for (const pattern of builderRoutes) {
    const match = matchPath(pattern, pathname);
    if (match) {
      let configId = Object.values(match.params).find(Boolean) || null;
      // Normalize numeric IDs to numbers for consistent comparisons (e.g., array.includes)
      if (configId && !isNaN(Number(configId))) {
        configId = Number(configId);
      }
      return { context: 'builder', configId };
    }
  }
  for (const route of listingRoutes) {
    if (matchPath(route, pathname)) {
      return { context: 'listing', configId: null };
    }
  }
  return null;
}

/**
 * Resolve the feature config for the current pathname.
 *
 * Inline features resolve synchronously. Lazy features resolve
 * synchronously if their config module is already loaded; otherwise this
 * throws a Promise so the nearest <Suspense> boundary shows its fallback
 * until the chunk arrives.
 *
 * @returns {{ config, context: 'listing'|'builder', configId: string|null } | null}
 */
export function getFeatureConfig(pathname) {
  // All features are lazy — sync route match, async config load.
  for (const { key, listingRoutes, builderRoutes } of LAZY_FEATURE_ROUTES) {
    const match = matchRoute(pathname, listingRoutes, builderRoutes);
    if (!match) continue;
    const config = getLazyFeatureConfigSync(key);
    if (config) return { config, ...match };
    // Not yet loaded — trigger load and suspend.
    throw loadLazyFeatureConfig(key);
  }
  return null;
}

// ---------------------------------------------------------------------------
// Look up any feature's config by key — used by useFeatureListData and
// WebTeams to render data from a feature that is NOT the route-active one.
//
// Inline keys resolve sync. Lazy keys suspend on first access (throw
// Promise) and resolve sync thereafter.
// ---------------------------------------------------------------------------

export function getFeatureApis(key) {
  if (LAZY_FEATURE_LOADERS[key]) {
    const cached = getLazyFeatureConfigSync(key);
    if (cached) return cached;
    throw loadLazyFeatureConfig(key);
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// FEATURE_KEYS — typed constants derived from the known keys so callers
// don't drift to magic strings.
// ---------------------------------------------------------------------------

const ALL_KEYS = Object.keys(LAZY_FEATURE_LOADERS);

const toConstCase = (camel) => camel.replace(/([a-z])([A-Z0-9])/g, '$1_$2').toUpperCase();

export const FEATURE_KEYS = Object.freeze(
  Object.fromEntries(ALL_KEYS.map((key) => [toConstCase(key), key]))
);
